/**
 * 参数压缩器 - 无需重新绘制的快速视频压缩
 * 
 * 特点：
 * ✅ 最快的处理速度
 * ✅ 无需canvas绘制
 * ✅ 保持原始时长
 * ❌ 压缩效果有限
 * ❌ 无法改变分辨率
 */

interface ParameterCompressOptions {
  maxSizeMB?: number;
  videoBitsPerSecond?: number;
  audioBitsPerSecond?: number;
  mimeType?: string;
  enableProgressLog?: boolean;
}

interface CompressResult {
  success: boolean;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  blob: Blob;
  processingTime: number;
  method: string;
  error?: string;
}

export class ParameterCompressor {
  /**
   * 参数调整压缩 - 直接调整MediaRecorder参数
   */
  static async compress(
    file: File,
    options: ParameterCompressOptions = {}
  ): Promise<CompressResult> {
    const startTime = Date.now();
    
    const {
      maxSizeMB = 30,
      videoBitsPerSecond,
      audioBitsPerSecond = 128000,
      mimeType,
      enableProgressLog = true
    } = options;

    try {
      // 创建video元素来获取原始流
      const video = document.createElement('video');
      video.src = URL.createObjectURL(file);
      video.muted = true;
      
      await new Promise((resolve, reject) => {
        video.onloadedmetadata = resolve;
        video.onerror = () => reject(new Error('视频加载失败'));
        setTimeout(() => reject(new Error('视频加载超时')), 10000);
      });

      // 根据文件大小智能选择比特率
      const targetVideoBitrate = videoBitsPerSecond || this.calculateOptimalBitrate(file.size);
      
      // 智能选择MIME类型
      const selectedMimeType = mimeType || this.selectBestMimeType();

      if (enableProgressLog) {
        console.log('🚀 参数压缩配置:', {
          原始大小: `${(file.size / (1024 * 1024)).toFixed(2)}MB`,
          目标大小: `${maxSizeMB}MB`,
          视频比特率: `${(targetVideoBitrate / 1000).toFixed(0)}kbps`,
          音频比特率: `${(audioBitsPerSecond / 1000).toFixed(0)}kbps`,
          编码格式: selectedMimeType,
          视频时长: `${video.duration?.toFixed(1)}秒`
        });
      }

      // 播放视频以获取流
      video.play();
      
      // 获取视频流
      const stream = this.captureVideoStream(video);
      
      // 配置MediaRecorder参数
      const recorderOptions: MediaRecorderOptions = {
        mimeType: selectedMimeType,
        videoBitsPerSecond: targetVideoBitrate,
        audioBitsPerSecond: audioBitsPerSecond
      };

      const compressedBlob = await this.recordVideo(stream, recorderOptions, video, enableProgressLog);
      
      // 清理资源
      video.pause();
      URL.revokeObjectURL(video.src);
      stream.getTracks().forEach(track => track.stop());

      const processingTime = Date.now() - startTime;
      const compressedSizeMB = compressedBlob.size / (1024 * 1024);

      const result: CompressResult = {
        success: true,
        originalSize: file.size,
        compressedSize: compressedBlob.size,
        compressionRatio: compressedBlob.size / file.size,
        blob: compressedBlob,
        processingTime,
        method: '参数调整压缩'
      };

      if (enableProgressLog) {
        console.log('✅ 参数压缩完成:', {
          原始大小: `${(file.size / (1024 * 1024)).toFixed(2)}MB`,
          压缩后大小: `${compressedSizeMB.toFixed(2)}MB`,
          压缩率: `${((1 - result.compressionRatio) * 100).toFixed(1)}%`,
          处理时间: `${(processingTime / 1000).toFixed(1)}秒`,
          是否超限: compressedSizeMB > maxSizeMB ? '⚠️ 是' : '✅ 否'
        });
      }

      // 检查压缩后大小
      if (compressedSizeMB > maxSizeMB) {
        console.warn(`⚠️ 压缩后文件仍超过限制: ${compressedSizeMB.toFixed(2)}MB > ${maxSizeMB}MB`);
        console.log('💡 建议: 降低比特率或使用其他压缩方法');
      }

      return result;

    } catch (error) {
      const processingTime = Date.now() - startTime;
      console.error('❌ 参数压缩失败:', error);
      
      return {
        success: false,
        originalSize: file.size,
        compressedSize: file.size,
        compressionRatio: 1,
        blob: new Blob([file], { type: file.type }),
        processingTime,
        method: '参数调整压缩',
        error: error instanceof Error ? error.message : '未知错误'
      };
    }
  }

  /**
   * 根据文件大小计算最优比特率
   */
  private static calculateOptimalBitrate(fileSize: number): number {
    const fileSizeMB = fileSize / (1024 * 1024);
    
    if (fileSizeMB <= 50) {
      // 小文件使用较高比特率
      return 2000000; // 2Mbps
    } else if (fileSizeMB <= 100) {
      // 轻度压缩
      return 1500000; // 1.5Mbps
    } else if (fileSizeMB <= 200) {
      // 标准压缩
      return 1000000; // 1Mbps
    } else if (fileSizeMB <= 500) {
      // 强力压缩
      return 600000; // 0.6Mbps
    } else {
      // 超大文件极限压缩
      return 400000; // 0.4Mbps
    }
  }

  /**
   * 选择最佳MIME类型
   */
  private static selectBestMimeType(): string {
    // 按效率优先级排序
    const supportedTypes = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8', 
      'video/mp4;codecs=h264',
      'video/webm'
    ];

    for (const type of supportedTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    // 默认回退
    return 'video/webm';
  }

  /**
   * 获取视频流
   */
  private static captureVideoStream(video: HTMLVideoElement): MediaStream {
    if ('captureStream' in video) {
      return (video as any).captureStream();
    } else if ('mozCaptureStream' in video) {
      return (video as any).mozCaptureStream();
    } else {
      throw new Error('浏览器不支持视频流捕获');
    }
  }

  /**
   * 录制视频
   */
  private static recordVideo(
    stream: MediaStream, 
    options: MediaRecorderOptions, 
    video: HTMLVideoElement,
    enableProgressLog: boolean
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const recorder = new MediaRecorder(stream, options);
      const chunks: Blob[] = [];
      let progressTimer: NodeJS.Timeout | null = null;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        if (progressTimer) {
          clearInterval(progressTimer);
        }
        const blob = new Blob(chunks, { type: options.mimeType });
        resolve(blob);
      };

      recorder.onerror = (event) => {
        if (progressTimer) {
          clearInterval(progressTimer);
        }
        console.error('❌ 录制错误:', event);
        reject(new Error('录制失败'));
      };

      // 开始录制
      recorder.start();

      // 进度日志
      if (enableProgressLog && video.duration) {
        progressTimer = setInterval(() => {
          const progress = (video.currentTime / video.duration * 100).toFixed(1);
          console.log(`⏳ 参数压缩进度: ${progress}%`);
        }, 5000); // 每5秒输出一次
      }

      // 等待视频播放完成
      video.onended = () => {
        setTimeout(() => {
          if (recorder.state === 'recording') {
            recorder.stop();
          }
        }, 100);
      };

      // 超时保护
      setTimeout(() => {
        if (recorder.state === 'recording') {
          console.warn('⚠️ 参数压缩超时，强制停止');
          recorder.stop();
        }
      }, 300000); // 5分钟超时
    });
  }

  /**
   * 批量压缩
   */
  static async compressBatch(
    files: File[], 
    options: ParameterCompressOptions = {}
  ): Promise<CompressResult[]> {
    const results: CompressResult[] = [];
    
    console.log(`🚀 开始批量参数压缩 ${files.length} 个文件`);
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      console.log(`⏳ 处理第 ${i + 1}/${files.length} 个文件: ${file.name}`);
      
      const result = await this.compress(file, {
        ...options,
        enableProgressLog: false // 批量处理时关闭详细日志
      });
      
      results.push(result);
      
      console.log(`${result.success ? '✅' : '❌'} 第 ${i + 1} 个文件处理完成`);
    }
    
    console.log('🎉 批量压缩完成');
    return results;
  }

  /**
   * 获取支持的MIME类型列表
   */
  static getSupportedMimeTypes(): string[] {
    const types = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/mp4;codecs=h264',
      'video/webm',
      'video/mp4'
    ];

    return types.filter(type => MediaRecorder.isTypeSupported(type));
  }

  /**
   * 估算压缩后大小
   */
  static estimateCompressedSize(
    originalSize: number, 
    duration: number, 
    targetBitrate: number
  ): number {
    // 估算公式: (视频比特率 + 音频比特率) × 时长 ÷ 8
    const videoBitrate = targetBitrate;
    const audioBitrate = 128000; // 128kbps
    const totalBitrate = videoBitrate + audioBitrate;
    
    return (totalBitrate * duration) / 8; // 转换为字节
  }
} 